import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, X } from 'lucide-react';

import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SearchableSelect } from '../ui/searchable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../ui/sonner';
import { accesClientsAPI, finDeJournee, jourDeFin } from '../../lib/accesClientsApi';
import { messageErreur } from '../../lib/erreurs';
import { formatDate } from '../../lib/formats';
import { libelle, nomLisible } from '../../lib/libelles';

const ETENDUES = ['client', 'camion', 'voyage'];

/**
 * Donne l'accès au portail à un client, ou change la date de fin d'un accès
 * existant. Étendue : tous les voyages du client, ceux d'un ou plusieurs
 * camions, ou un seul voyage — toujours limitée aux commandes de CE client.
 */
export const AccesDialog = ({ open, onOpenChange, acces, options, onDone }) => {
  const modification = Boolean(acces);
  const [compte, setCompte] = useState('');
  const [client, setClient] = useState('');
  const [etendue, setEtendue] = useState('client');
  const [camions, setCamions] = useState([]);
  const [voyage, setVoyage] = useState('');
  const [voyages, setVoyages] = useState([]);
  const [fin, setFin] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCompte(acces?.donneur_ordre_id || '');
    setClient(acces?.client_id || '');
    setEtendue(acces?.scope && ETENDUES.includes(acces.scope) ? acces.scope : 'client');
    setCamions(acces?.truck_ids || []);
    setVoyage(acces?.trip_id || '');
    setFin(jourDeFin(acces?.access_expires_at));
    setEnCours(false);
  }, [open, acces]);

  // Les voyages proposés sont ceux du client choisi.
  useEffect(() => {
    if (!open || modification || etendue !== 'voyage' || !client) {
      setVoyages([]);
      return undefined;
    }
    let actif = true;
    accesClientsAPI
      .voyagesDuClient(client)
      .then((liste) => actif && setVoyages(liste))
      .catch(() => actif && setVoyages([]));
    return () => {
      actif = false;
    };
  }, [open, modification, etendue, client]);

  const optionsComptes = useMemo(
    () => (options.portal_users || []).map((u) => ({ value: u.id, label: nomLisible(u.name, 'Compte sans nom'), keywords: [u.email] })),
    [options.portal_users],
  );
  const optionsClients = useMemo(
    () => (options.clients || []).map((c) => ({ value: c.id, label: nomLisible(c.name, 'Client sans nom') })),
    [options.clients],
  );
  const optionsCamions = useMemo(
    () =>
      (options.trucks || [])
        .filter((t) => !camions.includes(t.id))
        .map((t) => ({ value: t.id, label: t.label || 'Camion sans plaque' })),
    [options.trucks, camions],
  );
  const optionsVoyages = useMemo(
    () =>
      voyages.map((v) => ({
        value: v.id,
        label: [v.reference, v.plate].filter(Boolean).join(' · ') || 'Voyage',
        description: v.date ? formatDate(v.date) : undefined,
      })),
    [voyages],
  );
  const plaque = (id) => (options.trucks || []).find((t) => t.id === id)?.label || 'Camion';

  const incomplet =
    !compte ||
    !client ||
    (etendue === 'camion' && camions.length === 0) ||
    (etendue === 'voyage' && !voyage);

  const corps = () => ({
    donneur_ordre_id: compte,
    client_id: client,
    trip_id: etendue === 'voyage' ? voyage : null,
    fleet_scope_json: etendue === 'camion' ? { truck_ids: camions } : null,
    access_expires_at: finDeJournee(fin),
  });

  const envoyer = async (evenement) => {
    evenement.preventDefault();
    if (enCours || (!modification && incomplet)) return;
    setEnCours(true);
    try {
      if (modification) {
        await accesClientsAPI.modifier(acces.id, { access_expires_at: finDeJournee(fin) });
        toast.success("La date de fin est enregistrée");
      } else {
        await accesClientsAPI.donner(corps());
        toast.success("L'accès est donné");
      }
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(messageErreur(err, "L'accès n'a pas pu être enregistré"));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="acces-dialog">
        <DialogHeader>
          <DialogTitle>{modification ? "Modifier l'accès" : 'Donner un accès'}</DialogTitle>
          <DialogDescription className="sr-only">Accès d'un client au portail</DialogDescription>
        </DialogHeader>
        <form onSubmit={envoyer} className="space-y-4">
          {modification ? (
            <p className="text-sm" data-testid="acces-resume">
              <span className="font-semibold">{acces.donneur_ordre_name || 'Compte'}</span>
              {' · '}
              {acces.client_name || 'Client'}
            </p>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label>Compte du portail</Label>
                <SearchableSelect
                  className="input-base"
                  data-testid="acces-compte"
                  aria-label="Compte du portail"
                  placeholder="Choisir un compte"
                  searchPlaceholder="Rechercher un compte…"
                  emptyMessage="Aucun compte du portail"
                  value={compte}
                  onValueChange={setCompte}
                  options={optionsComptes}
                />
                {optionsComptes.length === 0 ? (
                  <p className="text-xs">
                    <Link to="/users" className="font-semibold text-primary underline-offset-2 hover:underline">
                      Créer un compte du portail
                    </Link>
                  </p>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label>Client</Label>
                <SearchableSelect
                  className="input-base"
                  data-testid="acces-client"
                  aria-label="Client"
                  placeholder="Choisir un client"
                  searchPlaceholder="Rechercher un client…"
                  emptyMessage="Aucun client trouvé"
                  value={client}
                  onValueChange={(valeur) => {
                    setClient(valeur);
                    setVoyage('');
                  }}
                  options={optionsClients}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Ce que le client voit</Label>
                <Select value={etendue} onValueChange={setEtendue}>
                  <SelectTrigger className="input-base" aria-label="Étendue de l'accès" data-testid="acces-etendue">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ETENDUES.map((code) => (
                      <SelectItem key={code} value={code}>
                        {libelle('portail_etendue', code)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {etendue === 'camion' ? (
                <div className="space-y-1.5">
                  <Label>Camions</Label>
                  <SearchableSelect
                    className="input-base"
                    data-testid="acces-camion"
                    aria-label="Camion"
                    placeholder="Ajouter un camion"
                    searchPlaceholder="Rechercher une plaque…"
                    emptyMessage="Aucun camion trouvé"
                    value=""
                    onValueChange={(valeur) => valeur && setCamions((liste) => [...liste, valeur])}
                    options={optionsCamions}
                  />
                  {camions.length ? (
                    <div className="flex flex-wrap gap-1.5" data-testid="acces-camions-choisis">
                      {camions.map((id) => (
                        <Badge key={id} variant="muted" className="gap-1">
                          {plaque(id)}
                          <button
                            type="button"
                            aria-label={`Retirer ${plaque(id)}`}
                            onClick={() => setCamions((liste) => liste.filter((item) => item !== id))}
                          >
                            <X className="h-3 w-3" aria-hidden="true" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {etendue === 'voyage' ? (
                <div className="space-y-1.5">
                  <Label>Voyage</Label>
                  <SearchableSelect
                    className="input-base"
                    data-testid="acces-voyage"
                    aria-label="Voyage"
                    placeholder={client ? 'Choisir un voyage' : "Choisissez d'abord le client"}
                    searchPlaceholder="Rechercher un voyage…"
                    emptyMessage="Aucun voyage pour ce client"
                    value={voyage}
                    onValueChange={setVoyage}
                    options={optionsVoyages}
                    disabled={!client}
                  />
                </div>
              ) : null}
            </>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="acces-fin">Fin de l'accès</Label>
            <Input
              id="acces-fin"
              type="date"
              className="input-base w-44"
              data-testid="acces-fin"
              value={fin}
              onChange={(e) => setFin(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || (!modification && incomplet)} data-testid="acces-enregistrer">
              {enCours ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {modification ? 'Enregistrer' : "Donner l'accès"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AccesDialog;
